"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = ["All", "Tax Planning", "Compliance", "Business Strategy", "Deductions", "Financial Health"];
function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return "";
  }
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function estimateReadTime(content?: string) {
  if (!content) return "5 min read";
  const words = stripHtml(content).split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}
function PostsConsumer({
  activeCategory
}: {
  activeCategory: string;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = usePosts() as any;
  if (loading) {
    return <div className="space-y-12">
        <div className="h-96 rounded-lg bg-muted/40 animate-pulse" />
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-96 rounded-lg bg-muted/40 animate-pulse" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-16">
        <p className="text-muted-foreground mb-4">
          Unable to load tax insights right now.
        </p>
        <Button onClick={refetch} variant="outline">
          Try again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-16">
        <p className="text-muted-foreground">
          No articles available in this category yet.
        </p>
      </div>;
  }
  const featured = posts[0];
  const rest = posts.slice(1);
  return <div className="space-y-16">
      {}
      <article className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center bg-card rounded-lg border border-border p-6 lg:p-8">
        <Link to={featured.link || "#"} className="block overflow-hidden rounded-lg aspect-[4/3] lg:aspect-[5/4]">
          {featured.featuredImage ? <img src={featured.featuredImage} alt={featured.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" /> : <div className="w-full h-full bg-muted" />}
        </Link>
        <div className="flex flex-col gap-5">
          {featured.categories && featured.categories[0] && <Badge className="bg-accent text-accent-foreground hover:bg-accent/90 self-start rounded-lg px-3 py-1 font-default">
              {featured.categories[0].name}
            </Badge>}
          <h3 className="font-default text-3xl md:text-4xl lg:text-[40px] leading-tight text-foreground">
            <Link to={featured.link || "#"} className="hover:underline decoration-accent underline-offset-4">
              {featured.title}
            </Link>
          </h3>
          <p className="text-base lg:text-lg text-foreground/70 line-clamp-2 leading-relaxed font-default">
            {stripHtml(featured.excerpt) || stripHtml(featured.content).slice(0, 180)}
          </p>
          <div className="flex items-center gap-3 pt-2">
            {featured.author && <>
                <SafeHydrate><Avatar className="size-10">
                  {featured.author.avatar && <AvatarImage src={featured.author.avatar["48"] || featured.author.avatar["96"] || ""} alt={featured.author.name} />}
                  <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                    {featured.author.name?.slice(0, 2).toUpperCase() || "AU"}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <span className="text-sm font-medium text-foreground font-default">
                  {featured.author.name}
                </span>
              </>}
            <span className="text-muted-foreground">•</span>
            <span className="flex items-center gap-1.5 text-sm text-foreground/60 font-default">
              <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
              {formatDate(featured.date)}
            </span>
          </div>
          <Link to={featured.link || "#"} className="inline-flex items-center gap-2 text-accent font-medium hover:gap-3 transition-all pt-2 font-default">
            Read insight
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </article>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {rest.map((post: any, i: number) => <article key={post.id} data-index={i} className="group bg-card rounded-lg border border-border overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col">
            <Link to={post.link || "#"} className="block overflow-hidden aspect-[16/10]">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" /> : <div className="w-full h-full bg-muted" />}
            </Link>
            <div className="p-8 flex flex-col gap-4 flex-1">
              {post.categories && post.categories[0] && <Badge className="bg-accent text-accent-foreground hover:bg-accent/90 self-start rounded-lg px-3 py-1 font-default">
                  {post.categories[0].name}
                </Badge>}
              <h3 className="font-default text-[22px] leading-snug text-foreground">
                <Link to={post.link || "#"} className="group-hover:underline decoration-accent underline-offset-4">
                  {post.title}
                </Link>
              </h3>
              <p className="text-sm text-foreground/70 line-clamp-2 leading-relaxed flex-1 font-default">
                {stripHtml(post.excerpt) || stripHtml(post.content).slice(0, 140)}
              </p>
              <div className="flex items-center gap-2 pt-4 border-t border-border/60 mt-auto">
                {post.author && <>
                    <SafeHydrate><Avatar className="size-7">
                      {post.author.avatar && <AvatarImage src={post.author.avatar["24"] || post.author.avatar["48"] || ""} alt={post.author.name} />}
                      <AvatarFallback className="bg-accent text-accent-foreground text-[10px]">
                        {post.author.name?.slice(0, 2).toUpperCase() || "AU"}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <span className="text-xs text-foreground/70 font-medium font-default">
                      {post.author.name}
                    </span>
                  </>}
                <span className="text-foreground/40 text-xs">•</span>
                <span className="flex items-center gap-1 text-xs text-foreground/60 font-default">
                  <Clock className="size-3" />
                  {estimateReadTime(post.content)}
                </span>
              </div>
            </div>
          </article>)}
      </div>

      {}
      {totalPages > 1 && <div className="flex flex-col items-center gap-8 pt-4">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <Button variant="ghost" size="sm" onClick={prevPage} disabled={!hasPrev} className="text-foreground hover:bg-accent disabled:opacity-40 font-default">
                  Previous
                </Button>
              </PaginationItem>
              {Array.from({
            length: Math.min(totalPages, 5)
          }).map((_, idx) => {
            const pageNum = idx + 1;
            return <PaginationItem key={pageNum} data-index={idx}>
                    <PaginationLink onClick={e => {
                e.preventDefault();
                setPage(pageNum);
              }} isActive={currentPage === pageNum} className="cursor-pointer border border-border text-foreground font-default">
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>;
          })}
              {totalPages > 5 && <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>}
              <PaginationItem>
                <Button variant="ghost" size="sm" onClick={nextPage} disabled={!hasNext} className="text-foreground hover:bg-accent disabled:opacity-40 font-default">
                  Next
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          <Link to="#" className="inline-flex items-center gap-2 text-accent font-medium hover:gap-3 transition-all font-default">
            Browse all insights
            <ArrowRight className="size-4" />
          </Link>
        </div>}
    </div>;
}
export default function Knowledge() {
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const wp_query_params: Record<string, any> = {
    post_type: "post",
    per_page: 7,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="687" id="knowledge-hub" className="bg-background py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <p className="text-accent text-sm font-medium tracking-[0.2em] uppercase mb-5 font-default">
              Tax Intelligence
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-[60px] leading-[1.05] text-foreground">
              Expert *guidance* for your financial clarity.
            </h2>
          </div>

          {}
          <div className="flex flex-wrap gap-2 lg:justify-end">
            {categories.map((cat, i) => <Button key={cat} data-index={i} variant={activeCategory === cat ? "default" : "ghost"} size="sm" onClick={() => setActiveCategory(cat)} className={activeCategory === cat ? "bg-accent text-accent-foreground hover:bg-accent/90 rounded-lg px-4 font-default" : "bg-accent/10 text-foreground hover:bg-accent/20 rounded-lg px-4 border border-border/60 font-default"}>
                {cat}
              </Button>)}
          </div>
        </div>

        <PostsProvider wp_query={wp_query}>
          <PostsConsumer activeCategory={activeCategory} />
        </PostsProvider>
      </div>
    </section>;
}